import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const legalModule: ChecklistModuleDefinition = {
  code: 'legal',
  name: 'Legal & Easements',
  description: 'Easements, deed restrictions, rights, and legal issues',
  categories: [
    {
      code: 'easements',
      name: 'Easements',
      items: [
        {
          code: 'easement_review',
          title: 'Easement discovery',
          description: 'Identify all easements on/through property',
          isGroupTask: false,
          dataFields: [
            { key: 'easements_found', label: 'Easements Found', type: 'boolean' },
            { key: 'easement_count', label: 'Number of Easements', type: 'number' },
            { key: 'easement_types', label: 'Easement Types', type: 'multiselect', options: [
              { value: 'utility', label: 'Utility Easement' },
              { value: 'access', label: 'Access/ROW Easement' },
              { value: 'drainage', label: 'Drainage Easement' },
              { value: 'conservation', label: 'Conservation Easement' },
              { value: 'pipeline', label: 'Pipeline Easement' },
              { value: 'powerline', label: 'Power Line Easement' },
              { value: 'telecommunication', label: 'Telecommunication' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Multiple easements present' },
          ],
        },
        {
          code: 'utility_easements',
          title: 'Utility easement review',
          description: 'Review utility company easements',
          isGroupTask: false,
          dataFields: [
            { key: 'utilities_present', label: 'Utilities with Easements', type: 'array' },
            { key: 'easement_width', label: 'Typical Width', type: 'number', unit: 'feet' },
            { key: 'location', label: 'Location on Property', type: 'string' },
            { key: 'impacts_use', label: 'Impacts Intended Use', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Utility easement impacts building site' },
          ],
        },
        {
          code: 'pipeline_easement',
          title: 'Pipeline/transmission easements',
          description: 'Check for pipeline or transmission easements',
          isGroupTask: false,
          dataFields: [
            { key: 'pipeline_exists', label: 'Pipeline Easement Exists', type: 'boolean' },
            { key: 'pipeline_type', label: 'Pipeline Type', type: 'string' },
            { key: 'easement_width', label: 'Easement Width', type: 'number', unit: 'feet' },
            { key: 'setback_required', label: 'Building Setback Required', type: 'number', unit: 'feet' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Major pipeline through property' },
            { flag: 'red', condition: 'Pipeline affects usable area' },
          ],
        },
        {
          code: 'conservation_easement',
          title: 'Conservation easement check',
          description: 'Check for conservation or preservation easements',
          isGroupTask: false,
          dataFields: [
            { key: 'conservation_easement', label: 'Conservation Easement', type: 'boolean' },
            { key: 'easement_holder', label: 'Easement Holder', type: 'string' },
            { key: 'restrictions', label: 'Restrictions', type: 'array' },
            { key: 'acres_affected', label: 'Acres Affected', type: 'number', unit: 'acres' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Conservation easement prevents intended use' },
            { flag: 'yellow', condition: 'Conservation easement limits development' },
            { flag: 'big_green', condition: 'Conservation easement provides tax benefits' },
          ],
        },
      ],
    },
    {
      code: 'deed_restrictions',
      name: 'Deed Restrictions',
      items: [
        {
          code: 'deed_restrictions_review',
          title: 'Deed restrictions review',
          description: 'Review all deed restrictions/covenants',
          isGroupTask: false,
          dataFields: [
            { key: 'restrictions_exist', label: 'Restrictions Exist', type: 'boolean' },
            { key: 'restriction_types', label: 'Restriction Types', type: 'array' },
            { key: 'enforcement', label: 'Enforcement Entity', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Restrictions prevent intended use' },
            { flag: 'yellow', condition: 'Some restrictions apply' },
          ],
        },
        {
          code: 'hoa_check',
          title: 'HOA/POA assessment',
          description: 'Check for homeowner/property owner association',
          isGroupTask: false,
          dataFields: [
            { key: 'hoa_exists', label: 'HOA/POA Exists', type: 'boolean' },
            { key: 'hoa_name', label: 'Association Name', type: 'string' },
            { key: 'monthly_dues', label: 'Monthly Dues', type: 'number', unit: 'dollars' },
            { key: 'special_assessments', label: 'Special Assessments', type: 'string' },
            { key: 'rules_reviewed', label: 'Rules Reviewed', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'HOA rules conflict with intended use' },
            { flag: 'yellow', condition: 'HOA restrictions to consider' },
          ],
        },
        {
          code: 'building_restrictions',
          title: 'Building restrictions',
          description: 'Restrictions on building type/size',
          isGroupTask: false,
          dataFields: [
            { key: 'min_sqft', label: 'Minimum Square Footage', type: 'number', unit: 'sq ft' },
            { key: 'max_stories', label: 'Maximum Stories', type: 'number' },
            { key: 'style_restrictions', label: 'Style Restrictions', type: 'string' },
            { key: 'material_restrictions', label: 'Material Restrictions', type: 'array' },
            { key: 'outbuilding_rules', label: 'Outbuilding Rules', type: 'string' },
          ],
        },
        {
          code: 'use_restrictions',
          title: 'Use restrictions',
          description: 'Restrictions on how property can be used',
          isGroupTask: false,
          dataFields: [
            { key: 'prohibited_uses', label: 'Prohibited Uses', type: 'array' },
            { key: 'livestock_allowed', label: 'Livestock Allowed', type: 'boolean' },
            { key: 'livestock_limits', label: 'Livestock Limits', type: 'string' },
            { key: 'commercial_allowed', label: 'Commercial Allowed', type: 'boolean' },
            { key: 'rental_restrictions', label: 'Rental Restrictions', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Intended use prohibited' },
            { flag: 'red', condition: 'Significant use restrictions' },
          ],
        },
      ],
    },
    {
      code: 'rights',
      name: 'Property Rights',
      items: [
        {
          code: 'mineral_rights',
          title: 'Mineral rights',
          description: 'Determine mineral rights status',
          isGroupTask: false,
          dataFields: [
            { key: 'mineral_rights_convey', label: 'Mineral Rights Convey', type: 'boolean' },
            { key: 'mineral_rights_severed', label: 'Rights Severed', type: 'boolean' },
            { key: 'severed_date', label: 'Severed Date', type: 'string' },
            { key: 'active_leases', label: 'Active Mining Leases', type: 'boolean' },
            { key: 'mineral_type', label: 'Mineral Types', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Active mining operations/leases' },
            { flag: 'red', condition: 'Mineral rights severed' },
            { flag: 'big_green', condition: 'Full mineral rights convey' },
          ],
        },
        {
          code: 'timber_rights',
          title: 'Timber rights',
          description: 'Verify timber rights status',
          isGroupTask: false,
          dataFields: [
            { key: 'timber_rights_convey', label: 'Timber Rights Convey', type: 'boolean' },
            { key: 'active_timber_lease', label: 'Active Timber Lease', type: 'boolean' },
            { key: 'lease_expiration', label: 'Lease Expiration', type: 'date' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Timber rights severed' },
            { flag: 'yellow', condition: 'Active timber lease' },
          ],
        },
        {
          code: 'water_rights',
          title: 'Water rights',
          description: 'Confirm water rights attached',
          isGroupTask: false,
          dataFields: [
            { key: 'water_rights_convey', label: 'Water Rights Convey', type: 'boolean' },
            { key: 'water_rights_type', label: 'Rights Type', type: 'string' },
            { key: 'allocation', label: 'Allocation', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'No water rights in water-critical area' },
            { flag: 'big_green', condition: 'Strong water rights convey' },
          ],
        },
        {
          code: 'hunting_fishing_rights',
          title: 'Hunting/fishing rights',
          description: 'Recreational rights status',
          isGroupTask: false,
          dataFields: [
            { key: 'rights_convey', label: 'Rights Convey', type: 'boolean' },
            { key: 'existing_leases', label: 'Existing Leases', type: 'boolean' },
            { key: 'lease_details', label: 'Lease Details', type: 'string' },
          ],
        },
        {
          code: 'air_rights',
          title: 'Air rights',
          description: 'Air rights and drone/overflight considerations',
          isGroupTask: false,
          dataFields: [
            { key: 'air_rights_intact', label: 'Air Rights Intact', type: 'boolean' },
            { key: 'flight_path_concerns', label: 'Flight Path Concerns', type: 'boolean' },
            { key: 'faa_restrictions', label: 'FAA Restrictions', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'legal_issues',
      name: 'Legal Issues',
      items: [
        {
          code: 'pending_litigation',
          title: 'Pending litigation',
          description: 'Check for any legal actions involving property',
          isGroupTask: false,
          dataFields: [
            { key: 'litigation_exists', label: 'Litigation Exists', type: 'boolean' },
            { key: 'litigation_type', label: 'Litigation Type', type: 'string' },
            { key: 'parties', label: 'Parties Involved', type: 'array' },
            { key: 'status', label: 'Status', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Active litigation' },
          ],
        },
        {
          code: 'code_violations',
          title: 'Code violations',
          description: 'Check for outstanding code violations',
          isGroupTask: false,
          dataFields: [
            { key: 'violations_exist', label: 'Violations Exist', type: 'boolean' },
            { key: 'violation_types', label: 'Violation Types', type: 'array' },
            { key: 'remediation_cost', label: 'Est. Remediation Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Outstanding code violations' },
          ],
        },
        {
          code: 'unpermitted_work',
          title: 'Unpermitted work',
          description: 'Identify any unpermitted structures/work',
          isGroupTask: false,
          dataFields: [
            { key: 'unpermitted_found', label: 'Unpermitted Work Found', type: 'boolean' },
            { key: 'unpermitted_items', label: 'Items', type: 'array' },
            { key: 'resolution_path', label: 'Resolution Path', type: 'string' },
            { key: 'estimated_cost', label: 'Est. Cost to Permit', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Significant unpermitted work' },
            { flag: 'yellow', condition: 'Minor unpermitted items' },
          ],
        },
      ],
    },
  ],
};

